import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { fontSize, palette } from '../..';
import { useLocation, useNavigate } from 'react-router-dom';
import HoverButton from '../Buttons/HoverButton';
import Logger from '../../Logger';
import { setApiResponse } from '../../redux/FlatFile/FlatFileStateActions';
import { SheetDialogResult } from '../../api/flatfile';

interface FileInfo {
  fileId: string;
  fileName: string;
}
interface SheetError {
  isError: boolean;
  message: string;
}

export const SheetDialog = ({
  sheetChooseDialogShow,
  loading,
  token,
  fileInfo,
  sheetLoading,
  sheetError,
  sheetNames,
  parentId,

  setLoading,
  setSheetChooseDialogShow,
  setErrorDialogOpen,
  setErrorDialogMessage,
  setSheetError,
  setSelectedFile,
  setDataToEditApiResponse,
}: {
  sheetChooseDialogShow: boolean;
  loading: boolean;
  token: string;
  fileInfo: FileInfo;
  sheetLoading: boolean;
  sheetError: SheetError;
  sheetNames: string[];
  parentId: string | undefined;

  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setSheetChooseDialogShow: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorDialogMessage: React.Dispatch<React.SetStateAction<string>>;
  setSheetError: React.Dispatch<
    React.SetStateAction<{
      isError: boolean;
      message: string;
    }>
  >;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | undefined>>;
  setDataToEditApiResponse: React.Dispatch<React.SetStateAction<any>>;
}) => {
  const navigate = useNavigate();
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const location = useLocation();
  const uploadExcelFile = async () => {
    const formData = new FormData();
    formData.append('fileId', fileInfo.fileId);
    formData.append('sheetName', selectedSheet);
    formData.append('fileName', fileInfo.fileName);
    setLoading(true);

    const res = await SheetDialogResult(formData);

    if (!res.isError) {
      setApiResponse(res.data);
      setDataToEditApiResponse(res.data);
      setLoading(false);
      if (location.state?.mode === 'update') {
        navigate(`/editflatfile/${parentId}`, {
          state: {
            mode: 'update',
            parentId: parentId,
            fileId: location.state.fileId,
            file: { ...res.data, fileType: 'excel' },
          },
        });
      } else {
        navigate(`/editflatfile/${parentId}`, {
          state: { mode: 'New', parentId: parentId },
        });
      }
    } else {
      setLoading(false);
      Logger('info', 'error');
      setErrorDialogMessage('Error uploading the file');
      setErrorDialogOpen(true);
      setSelectedFile(undefined); // reset selectedFile after error
      // setSelectedSheetName("");
      setSelectedSheet('');
    }
  };

  return (
    <Dialog
      open={sheetChooseDialogShow}
      disableEnforceFocus
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        background: 'transparent',
      }}
      TransitionProps={{ timeout: 0 }}
      // onClose={() => setSheetChooseDialogShow(false)}
    >
      <Box
        sx={{
          height: '20rem',
          width: '20rem',
          padding: '1rem',
        }}
      >
        {sheetLoading ? (
          <div
            className="loading-container"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBlock: 0,
              height: '100%',
              width: '100%',
            }}
          >
            <div className="user-spinner"></div>
          </div>
        ) : sheetError.isError ? (
          <Box
            sx={{
              width: '100%',
            }}
          >
            <Typography
              sx={{
                color: 'red',
              }}
            >
              Some Error occurred while fetching sheet names
            </Typography>
          </Box>
        ) : (
          <Box>
            <DialogTitle
              id="alert-dialog-title"
              sx={{ fontSize: fontSize.large, fontWeight: 'bold', paddingInline: 0 }}
            >
              Select Sheet
            </DialogTitle>
            <Box>
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">Sheet name</InputLabel>
                <Select
                  value={selectedSheet}
                  labelId="demo-simple-select-label"
                  onChange={(e) => setSelectedSheet(e.target.value)}
                  label="Sheet name"
                  sx={{ width: '100%' }}
                >
                  {sheetNames.map((sheetName: string) => (
                    <MenuItem key={sheetName} value={sheetName}>
                      {sheetName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}
        {!sheetLoading ? (
          <Box
            sx={{
              position: 'absolute',
              width: 'calc(100% - 2rem);',
              bottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '1rem',
            }}
          >
            <HoverButton
              text="Cancel"
              color="red"
              transitionTime="0.2s"
              backgroundColor="secondary.contrastText"
              hoverColor="secondary.contrastText"
              hoverBackgroundColor="red"
              sx={{
                border: '1px solid red',
              }}
              onClick={() => {
                setSheetChooseDialogShow(false);
                setSelectedFile(undefined);
                setSheetError({ isError: false, message: '' });
                setSelectedSheet('');
              }}
            />
            <Tooltip
              title={!selectedSheet ? 'Please select a sheet' : ''}
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <span>
                <HoverButton
                  disabled={sheetError.isError || !selectedSheet}
                  text="Proceed"
                  color="primary.main"
                  transitionTime="0.2s"
                  backgroundColor="secondary.contrastText"
                  hoverColor="secondary.contrastText"
                  hoverBackgroundColor="primary.main"
                  sx={{
                    border: `1px solid ${
                      sheetError.isError || !selectedSheet ? '#9e9e9e' : palette.primary.main
                    }`,
                    cursor: sheetError.isError || !selectedSheet ? 'not-allowed' : 'pointer',
                  }}
                  onClick={() => {
                    if (!selectedSheet) return;
                    setSheetChooseDialogShow(false);
                    uploadExcelFile();
                  }}
                />
              </span>
            </Tooltip>
          </Box>
        ) : null}
      </Box>
    </Dialog>
  );
};
